"use client";

import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { LoadingSpinner } from "@/components/ui/spinner";
import { uploadPDF } from "@/actions/upload";
import { GO_REST_URL } from "@/lib/constants";
import { Label } from "@/components/ui/label";

export default function PDFEncrypterDecrypter() {
  const [selectedFile, setSelectedFile] = useState<File>();
  const [processedFile, setProccesedFile] = useState<{
    file: Blob | null;
    mimeType: string | null;
  }>({ file: null, mimeType: null });

  const [action, setAction] = useState<"Encrypt" | "Decrypt" | "">("");
  const [password, setPassword] = useState<string>("");
  const [processState, setProcessState] = useState<"ready" | "processing">(
    "ready"
  );

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setProccesedFile({ file: null, mimeType: null });
  };

  const handleSubmit = async () => {
    if (password.length < 8) {
      toast({
        variant: "destructive",
        title: "Uh oh! Password must of at least 8 characters",
      });
      return;
    }
    if (!selectedFile) {
      toast({
        variant: "destructive",
        title: "Uh oh! Please select a PDF file.",
      });
      return;
    }
    setProcessState("processing");
    try {
      const formData = new FormData();
      formData.append("pdf_file", selectedFile);
      formData.append("owner_password", password);
      formData.append("user_password", password);

      const path =
        action === "Encrypt"
          ? `${GO_REST_URL}/v1/encrypt-pdf`
          : `${GO_REST_URL}/v1/decrypt-pdf`;

      const response = await uploadPDF(formData, path);
      if (response.file instanceof Blob) {
        setProccesedFile({ file: response.file, mimeType: response.mimeType });
      }
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: "There was a problem with your request.",
        action: <ToastAction altText="Try again">Try again</ToastAction>,
      });
    } finally {
      setProcessState("ready");
    }
  };

  const handleDownloadFile = () => {
    if (processedFile.file && processedFile.mimeType) {
      const downloadLink = URL.createObjectURL(processedFile.file);
      const ext = processedFile.mimeType.split("/")[1];
      const a = document.createElement("a");
      a.href = downloadLink;
      a.download = `${Date.now()}--file.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      toast({
        title: "Uh oh! Failed to download.",
        description: "Sorry not file to download",
        action: <ToastAction altText="Try again">Try again</ToastAction>,
      });
    }
  };

  const handleClear = () => {
    setProccesedFile({ file: null, mimeType: null });
    setAction("");
  };

  return (
    <main className="flex flex-col items-center justify-between gap-4">
      <div className="w-72">
        <Label htmlFor="fileInput">
          {action ? `Pdf to ${action}` : "File PDF"}
        </Label>
        <Input
          id="fileInput"
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
        />
      </div>

      <div className="flex gap-4 sm:flex-col items-center">
        <Select
          value={action || ""}
          onValueChange={(value) => setAction(value as "Encrypt" | "Decrypt")}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Encrypt">Encrypt</SelectItem>
            <SelectItem value="Decrypt">Decrypt</SelectItem>
          </SelectContent>
        </Select>

        <div className="w-52">
          <Input
            type="text"
            placeholder="Enter a Passpharse"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
      </div>

      <div className="flex gap-2">
        {processState == "processing" ? (
          <div className="flex gap-2">
            <LoadingSpinner /> {action + "ing"}...
          </div>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={
              processState !== "ready" || password.length <= 3 || !action
            }
          >
            {"Upload and Submit"}
          </Button>
        )}
        {processedFile.file && (
          <Button variant={"outline"} onClick={handleClear}>
            Clear
          </Button>
        )}
      </div>

      {processedFile.file && (
        <div className="flex flex-col gap-2 items-center">
          <p className="mt-2">
            <span>
              File Size: {(processedFile.file.size / 1024).toFixed(1)} KB
            </span>
            <span> File Type: {processedFile.file.type}</span>
          </p>
          <div>
            <Button
              variant={"secondary"}
              onClick={handleDownloadFile}
              disabled={processState != "ready"}
            >
              Download File
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}
